import { Component, OnDestroy, OnInit } from '@angular/core';
import { StoreKeys, SyncRemoteDataService } from '@rappider/services';
import { PROJECT_EDIT_FORM_CONFIG } from '@rappider/shared/configs';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { UpdateProject } from '../../states/projects-state/project.actions';
import { defaultToolbarTitleHeadingSize, PAGE_DEFINITIONS, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { BreadcrumbOption, HeadingComponentConfig } from '@rappider/rappider-components/utils';
import { Project } from '@rappider/rappider-sdk';
import { Subscription, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'rappider-project-edit',
  templateUrl: './project-edit.component.html',
  styleUrls: ['./project-edit.component.scss']
})
export class ProjectEditComponent implements OnInit, OnDestroy {
  PROJECT_EDIT_FORM_CONFIG = PROJECT_EDIT_FORM_CONFIG;
  mainTitle: HeadingComponentConfig = {
    content: 'PROJECT_MODULE.PROJECT_EDIT_COMPONENT.PROJECT_EDIT',
    type: defaultToolbarTitleHeadingSize
  };
  title: string | string[] | BreadcrumbOption[];

  projectId: string;
  project: Project;
  editedProjectData: any;
  subscriptions: Subscription[];
  displayToolbar = false;
  displayToolbarBackButton = false;
  isLoading = false;
  private pollingProject$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<any>,
    private syncRemoteDataService: SyncRemoteDataService
  ) { }

  /**
   *
   *
   * @memberof ProjectEditComponent
   */
  ngOnInit(): void {
    this.projectId = this.getProjectId();
    this.subscribeToData();
    this.startPolling();
    if (this.projectId) {
      this.syncRemoteDataService.syncRemoteDataItemWithKey(this.projectId, StoreKeys.Project);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingProject$.next();
    this.pollingProject$.complete();
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToProjectLoading(),
      this.subscribeToActiveProject(),
      this.subscribeToProjectCategories()
    ];
  }

  /**
   * Takes the project id paramater from route
   *
   * @returns
   * @memberof ProjectEditComponent
   */
  getProjectId() {
    return this.activatedRoute.snapshot.params['projectId'];
  }

  subscribeToProjectLoading() {
    return this.store.select(state => state.project?.loading).subscribe(loading => {
      this.isLoading = loading;
    });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject?.data).subscribe(activeProject => {
      if (this.projectId && activeProject && activeProject.id === this.projectId) {
        this.project = activeProject;
        this.setProjectTitle();
      }
      if (activeProject?.id) {
        this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProject.id, StoreKeys.Project);
      }
    });
  }



  setProjectTitle() {
    this.title = [
      {
        label: this.project.name ?? '',
        redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.project?.id}`
      },
      {
        label: 'PROJECT_MODULE.PROJECT_EDIT_COMPONENT.PROJECT_EDIT'
      }
    ];
  }

  startPolling() {
    interval(5000).pipe(
      takeUntil(this.pollingProject$)
    ).subscribe(() => {
      if (this.projectId) {
        this.syncRemoteDataService.syncRemoteDataItemWithKey(this.projectId, StoreKeys.Project);
      }
    });
  }

  subscribeToProjectCategories() {
    return this.store.select(state => state.project.publicProjectCategories).subscribe(categories => {
      const categoryInput = this.PROJECT_EDIT_FORM_CONFIG.items.find(item => item.fieldName === 'categoryIds');
      categoryInput.options = categories?.map(category => ({
        key: category.title,
        value: category.id
      }));

      this.PROJECT_EDIT_FORM_CONFIG = { ... this.PROJECT_EDIT_FORM_CONFIG };
    });
  }

  /**
   * Fire's when save button clicked
   *
   * @param {ProjectInterface} project
   * @memberof ProjectEditComponent
   */
  editProject(project: Partial<Project>) {
    if (this.projectId) {
      const categoryIds = Array.isArray(project.categoryIds) ? project.categoryIds : (project.categoryIds ? [project.categoryIds] : []);
      this.store.dispatch(new UpdateProject({
        project: {
          name: project.name,
          key: project.key,
          description: project.description,
          categoryIds: categoryIds,
          metadata: project.metadata
        },
        projectId: this.projectId
      }));
    }
  }

  onFieldValueChange(data: any) {
    this.editedProjectData = {
      ...this.editedProjectData,
      ...data
    };
  }

}
